class GlymedSlideshow extends HTMLElement {
  // ==========================================
  // 1. LIFECYCLE
  // ==========================================
  constructor() {
    super();
    this.slidesWrapper = this.querySelector('.slideshow-wrapper');
    this.slides = this.querySelectorAll('.slideshow-slide');
    this.dots = this.querySelectorAll('.pagination-dot');
    this.totalSlides = this.slides.length;
    this.currentSlide = 1;
    
    // Autoplay configuration
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.speed) * 1000;
    this.timer = null;
    this.isHovering = false;
    this.isAnimating = false;

    // Drag tracking variables
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.draggedDistance = 0;
    this.isScrolling = undefined;

    // Navigation elements
    this.prevBtn = this.querySelector('.slideshow-arrow--prev');
    this.nextBtn = this.querySelector('.slideshow-arrow--next');

    // Method binding
    this.nextSlide = this.nextSlide.bind(this);
    this.prevSlide = this.prevSlide.bind(this);
    this.handleDotClick = this.handleDotClick.bind(this);
    this.dragStart = this.dragStart.bind(this);
    this.dragMove = this.dragMove.bind(this);
    this.dragEnd = this.dragEnd.bind(this);

    this.init();
  }

  disconnectedCallback() {
    this.pauseAutoplay();
  }

  // ==========================================
  // 2. INITIALIZATION & BINDING
  // ==========================================
  init() {
    if (this.totalSlides < 2) return;

    // Seamless Loop Setup: Clone Last (Prepend) and First (Append)
    const firstClone = this.slides[0].cloneNode(true);
    const lastClone = this.slides[this.totalSlides - 1].cloneNode(true);
    
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');

    [firstClone, lastClone].forEach(clone => {
      clone.querySelectorAll('h1').forEach(h1 => {
        const h2 = document.createElement('h2');
        h2.innerHTML = h1.innerHTML;
        Array.from(h1.attributes).forEach(attr => h2.setAttribute(attr.name, attr.value));
        h1.parentNode.replaceChild(h2, h1);
      });
    });

    this.slidesWrapper.prepend(lastClone);
    this.slidesWrapper.appendChild(firstClone);

    // Initial Position (Show real slide 1)
    this.updateSlider(false);

    // Bind Button Navigation
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => {
      this.pauseAutoplay();
      this.prevSlide();
    });
    
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => {
      this.pauseAutoplay();
      this.nextSlide();
    });

    // Bind Dots
    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.handleDotClick);
    });

    // Bind Drag / Swipe Listeners
    this.slidesWrapper.style.cursor = 'grab';
    this.slidesWrapper.addEventListener('touchstart', this.dragStart, { passive: true });
    this.slidesWrapper.addEventListener('touchmove', this.dragMove, { passive: false });
    this.slidesWrapper.addEventListener('touchend', this.dragEnd);
    
    this.slidesWrapper.addEventListener('mousedown', this.dragStart);
    this.slidesWrapper.addEventListener('mousemove', this.dragMove);
    this.slidesWrapper.addEventListener('mouseup', this.dragEnd);
    this.slidesWrapper.addEventListener('mouseleave', this.dragEnd);

    // Prevent accidental link clicks when a drag maneuver occurs
    this.slidesWrapper.addEventListener('click', (e) => {
      if (Math.abs(this.draggedDistance) > 5) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, { capture: true });

    this.slidesWrapper.querySelectorAll('img, a').forEach(el => {
      el.addEventListener('dragstart', (e) => e.preventDefault());
    });

    // Initialize Autoplay
    if (this.autoplayEnabled) {
      this.startAutoplay();
      
      this.addEventListener('mouseenter', () => {
        this.isHovering = true;
        this.pauseAutoplay();
      });
      
      this.addEventListener('mouseleave', () => {
        this.isHovering = false;
        if (!this.isDragging) this.startAutoplay();
      });
    }
  }

  // ==========================================
  // 3. CORE RENDERING ENGINE
  // ==========================================
  updateSlider(enableTransition = true) {
    if (enableTransition) {
      this.slidesWrapper.style.transition = 'transform 0.5s ease-in-out';
    } else {
      this.slidesWrapper.style.transition = 'none';
    }

    const translateX = -(this.currentSlide * 100);
    this.slidesWrapper.style.transform = `translateX(${translateX}%)`;
    
    // Sync Dots
    let dotIndex = this.currentSlide - 1;
    if (this.currentSlide === 0) dotIndex = this.totalSlides - 1;
    if (this.currentSlide === this.totalSlides + 1) dotIndex = 0;

    this.dots.forEach(dot => dot.classList.remove('active'));
    if(this.dots[dotIndex]) {
        this.dots[dotIndex].classList.add('active');
    }
  }

  // ==========================================
  // 4. NAVIGATION CONTROLS
  // ==========================================
  nextSlide() {
    if (this.isAnimating) return; 

    this.currentSlide++;
    this.updateSlider(true);

    // Hardcut snap backwards if we hit the clone illusion
    if (this.currentSlide === this.totalSlides + 1) {
      this.isAnimating = true;
      setTimeout(() => {
        this.currentSlide = 1; 
        this.updateSlider(false);
        this.isAnimating = false;
      }, 500);
    }
  }

  prevSlide() {
    if (this.isAnimating) return;

    this.currentSlide--;
    this.updateSlider(true);

    if (this.currentSlide === 0) {
      this.isAnimating = true;
      setTimeout(() => {
        this.currentSlide = this.totalSlides; 
        this.updateSlider(false);
        this.isAnimating = false;
      }, 500);
    }
  }

  handleDotClick(e) {
    if (this.isAnimating) return;
    this.pauseAutoplay();
    const index = parseInt(e.target.dataset.index);
    this.currentSlide = index + 1;
    this.updateSlider(true);
  }

  // ==========================================
  // 5. DRAG & SWIPE PHYSICS
  // ==========================================
  dragStart(e) {
    if (this.isAnimating) return;
    this.isDragging = true;
    this.pauseAutoplay();
    this.slidesWrapper.classList.add('is-dragging');
    this.slidesWrapper.style.cursor = 'grabbing';
    
    this.dragStartX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    this.dragStartY = e.type.includes('mouse') ? e.pageY : e.touches[0].clientY;
    this.draggedDistance = 0;
    this.isScrolling = undefined;
    
    this.slidesWrapper.style.transition = 'none';
  }

  dragMove(e) {
    if (!this.isDragging) return;
    
    const currentX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    const currentY = e.type.includes('mouse') ? e.pageY : e.touches[0].clientY;
    
    if (typeof this.isScrolling === 'undefined') {
      this.isScrolling = Math.abs(currentY - this.dragStartY) > Math.abs(currentX - this.dragStartX);
    }

    if (this.isScrolling) return; 
    
    e.preventDefault(); 

    this.draggedDistance = currentX - this.dragStartX;
    const translateX = -(this.currentSlide * 100);
    const wrapperWidth = this.slidesWrapper.offsetWidth;
    const percentageDragged = (this.draggedDistance / wrapperWidth) * 100;
    
    this.slidesWrapper.style.transform = `translateX(${translateX + percentageDragged}%)`;
  }

  dragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.slidesWrapper.classList.remove('is-dragging');
    this.slidesWrapper.style.cursor = 'grab';
    
    if (this.isScrolling) {
      this.updateSlider(true);
      return;
    }

    const threshold = 15;
    const wrapperWidth = this.slidesWrapper.offsetWidth;
    const percentageDragged = (this.draggedDistance / wrapperWidth) * 100;

    if (percentageDragged < -threshold) {
      this.nextSlide();
    } else if (percentageDragged > threshold) {
      this.prevSlide();
    } else {
      this.updateSlider(true); 
    }

    if (this.autoplayEnabled && !this.isHovering) {
      this.startAutoplay();
    }
  }

  // ==========================================
  // 6. AUTOPLAY MANAGEMENT
  // ==========================================
  startAutoplay() {
    if (this.isHovering || this.isDragging) return;
    if (!this.timer) {
      this.timer = setInterval(this.nextSlide, this.autoplaySpeed);
    }
  }

  pauseAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

customElements.define('glymed-slideshow', GlymedSlideshow);